import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import CONDITIONS_CONFIG, { CONDITION_TYPES } from './conditionsConfig';

const OPERATOR_LABELS = {
  '=': 'Equal ( = )',
  '!=': 'Not Equal ( != )',
  '>': 'Greater Than ( > )',
  '<': 'Less Than ( < )',
  '>=': 'Greater or Equal ( >= )',
  '<=': 'Less or Equal ( <= )',
  contains: 'Contains',
  not_contains: 'Not Contains',
};

const styles = {
  conditionRow: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    p: 2,
    backgroundColor: '#fafafa',
  },
  dividerLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    my: 1,
  },
  dividerLine: {
    flex: 1,
    borderColor: '#ccc',
  },
};

function LogicalOperatorDivider({ value, onChange }) {
  return (
    <Box sx={styles.dividerLabel}>
      <Divider sx={styles.dividerLine} />
      <Select
        variant='standard'
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: '#888',
          minWidth: 60,
        }}
        disableUnderline>
        <MenuItem value='AND'>AND</MenuItem>
        <MenuItem value='OR'>OR</MenuItem>
      </Select>
      <Divider sx={styles.dividerLine} />
    </Box>
  );
}

function ConditionRow({ condition, onUpdate, onRemove }) {
  const colConfig = CONDITIONS_CONFIG.find((c) => c.field === condition.field);
  const operators = colConfig?.operators ?? [];
  const isMultiSelect = colConfig?.type === CONDITION_TYPES.MULTI_SELECT;
  const isOptions = isMultiSelect || colConfig?.type === CONDITION_TYPES.SELECT;
  const [valueOptions, setValueOptions] = useState([]);

  useEffect(() => {
    if (!colConfig || !colConfig.values) {
      setValueOptions([]);
      return;
    }
    colConfig.values().then(setValueOptions).catch(console.error);
  }, [condition.field]);

  const handleColumnChange = (e) => {
    onUpdate(condition.id, 'field', e.target.value);
    onUpdate(condition.id, 'operator', '');
    const nextConfig = CONDITIONS_CONFIG.find(
      (c) => c.field === e.target.value,
    );
    onUpdate(
      condition.id,
      'value',
      nextConfig?.type === CONDITION_TYPES.MULTI_SELECT ? [] : '',
    );
  };

  return (
    <Box sx={styles.conditionRow}>
      <GridContainer alignItems='flex-end'>
        <GridItem xs={12} sm={12} md={4}>
          <div className='custom-select'>
            <FormControl variant='standard' fullWidth>
              <InputLabel id={`col-${condition.id}`}>Column</InputLabel>
              <Select
                labelId={`col-${condition.id}`}
                value={condition.field}
                onChange={handleColumnChange}>
                {CONDITIONS_CONFIG.map((cfg) => (
                  <MenuItem key={cfg.field} value={cfg.field}>
                    {cfg.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </GridItem>

        <GridItem xs={12} sm={12} md={4}>
          <div className='custom-select'>
            <FormControl variant='standard' fullWidth>
              <InputLabel id={`op-${condition.id}`}>Operator</InputLabel>
              <Select
                labelId={`op-${condition.id}`}
                value={condition.operator}
                onChange={(e) =>
                  onUpdate(condition.id, 'operator', e.target.value)
                }
                disabled={!colConfig}>
                {operators.map((op) => (
                  <MenuItem key={op} value={op}>
                    {OPERATOR_LABELS[op] ?? op}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </GridItem>

        <GridItem xs={10} sm={10} md={3}>
          {isOptions ? (
            <div className='custom-select'>
              <FormControl variant='standard' fullWidth>
                <InputLabel id={`val-${condition.id}`}>Value</InputLabel>
                <Select
                  labelId={`val-${condition.id}`}
                  multiple={isMultiSelect}
                  value={
                    Array.isArray(condition.value)
                      ? condition.value
                      : condition.value
                      ? [condition.value]
                      : []
                  }
                  onChange={(e) =>
                    onUpdate(condition.id, 'value', e.target.value)
                  }
                  disabled={!condition.operator}
                  renderValue={
                    isMultiSelect
                      ? (selected) => (
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 0.5,
                            }}>
                            {selected.map((val) => {
                              const opt = valueOptions.find(
                                (o) => o.value === val,
                              );
                              return (
                                <Chip
                                  key={val}
                                  label={opt?.label ?? val}
                                  size='small'
                                />
                              );
                            })}
                          </Box>
                        )
                      : undefined
                  }>
                  {valueOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          ) : (
            <TextField
              variant='standard'
              fullWidth
              label='Value'
              type={
                colConfig?.type === CONDITION_TYPES.NUMBER ? 'number' : 'text'
              }
              value={condition.value}
              onChange={(e) => onUpdate(condition.id, 'value', e.target.value)}
              disabled={!condition.operator}
            />
          )}
        </GridItem>

        <GridItem xs={2} sm={2} md={1}>
          <IconButton
            color='error'
            onClick={() => onRemove(condition.id)}
            sx={{ mb: 0.5 }}>
            <RemoveCircleOutlineIcon />
          </IconButton>
        </GridItem>
      </GridContainer>
    </Box>
  );
}

export default function ConditionsSection({
  conditions,
  onAdd,
  onUpdate,
  onRemove,
  error,
}) {
  return (
    <Box>
      {conditions.map((condition, index) => (
        <Box key={condition.id}>
          {index > 0 && (
            <LogicalOperatorDivider
              value={conditions[index - 1].logicalOperator}
              onChange={(val) =>
                onUpdate(conditions[index - 1].id, 'logicalOperator', val)
              }
            />
          )}
          <ConditionRow
            condition={condition}
            onUpdate={onUpdate}
            onRemove={onRemove}
          />
        </Box>
      ))}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant='outlined'
          size='small'
          startIcon={<AddIcon />}
          onClick={onAdd}>
          Add Condition
        </Button>
      </Box>
      {error && (
        <Box sx={{ mt: 1, color: 'error.main', fontSize: '0.75rem' }}>
          {error}
        </Box>
      )}
    </Box>
  );
}
